<!--
  资源监控模块（概览页 - 运维监控下方）
  - 顶部：带宽（出口）下拉单选 + 时间范围筛选
  - 主体：流量折线图，复用 DynamicEnvChart
  - 接口逻辑参考 /bandwidthtraffic/detail/:id?tabName=monitor 中的"监控信息"Tab
-->
<template>
  <div class="resource-monitor" v-loading="loading">
    <h3 class="monitor-title">{{ $t('overview.resourceMonitor.title') }}</h3>

    <!-- 带宽下拉 -->
    <el-row class="filter-row">
      <span class="filter-label">{{ $t('overview.resourceMonitor.bandwidthLabel') | withColon }}&nbsp;</span>
      <el-select
        v-model="selectedBandwidthId"
        filterable
        :placeholder="$t('placeholder.choose')"
        @change="onBandwidthChange"
        style="width: 220px;">
        <el-option
          v-for="item in bandwidthList"
          :key="item.bandwidthId"
          :label="item.bandwidthName"
          :value="item.bandwidthId">
        </el-option>
      </el-select>
    </el-row>

    <!-- 时间范围筛选 -->
    <el-row class="filter-row" v-if="bandwidthList.length">
      <date-range
        ref="dateRange"
        :default-value="defaultValue"
        max-range-days="90"
        shortcut-days="1h,6h,12h,24h,3d,7d,14d,30d"
        :is-linkage-shortcuts="false"
        :use-whole-day-shortcuts="false"
        v-model="dateRangeValue"
        @change="onDateRangeChange(false)"
        style="display:inline-block"/>
      <span :class="['date-range-tips',{'date-range-color': dateRangeTips}]">
        ({{ $t('overview.resourceMonitor.day31range') }})
      </span>
    </el-row>

    <!-- 折线图 -->
    <div class="chart-box" v-loading="chartLoading" v-if="bandwidthList.length">
      <!-- <div class="chart-header" v-if="dynamicEnvData">
        <p class="period">{{ $t('overview.resourceMonitor.period') | withColon }} {{ periodTag }}</p>
      </div> -->
      <div class="chart-main">
        <dynamic-env-chart
          v-if="dynamicEnvData"
          :electric-currents="dynamicEnvData"
          :is-empty="isChartEmpty"
          :periodTag="periodTag"
          :switchPortIndex="'all'"
          :dateRangeValue="dateRangeValue"
          :series-names="seriesNames"
          :y-axis-name="yAxisName"
          :zoom-bar="false"
          unit="Mbps"
          width="100%"/>
        <el-empty v-else :description="$t('text.noSearch')"></el-empty>
      </div>
    </div>

    <!-- 无可监控带宽时兜底 -->
    <el-empty
      v-if="!loading && !bandwidthList.length"
      :description="$t('overview.resourceMonitor.noBandwidthTip')">
    </el-empty>
  </div>
</template>

<script>
import DateRange from '@/components/common/DateRange';
import DynamicEnvChart from '@/components/common/DynamicEnvChart';
import ChartHelper from '@/components/bandwidthTraffic/chart.helper';

const ONE_HOUR = 3600 * 1000;
const MAX_DATE_RANGE = 31; // 最多连续31天
const DEFAULT_PAGE_SIZE = 100;
const BANDWIDTH_TRAFFIC_CANCEL_NAME = 'getBandwidthTrafficInfoCancel';

export default {
  name: 'ResourceMonitor',
  components: {
    DateRange,
    DynamicEnvChart
  },
  data() {
    return {
      loading: false,
      chartLoading: false,
      // 带宽下拉数据
      bandwidthList: [],
      // 选中带宽
      selectedBandwidthId: '',
      selectedBandwidth: null,
      // 时间范围
      defaultValue: '24h',
      dateRangeValue: null,
      dateRangeTips: false,
      // 周期
      periodTag: '--',
      // 图表数据
      dynamicEnvData: null,
      isChartEmpty: false,
      yAxisName: '',
      seriesNames: '',
      // 轮询/防并发
      monitorPollTimer: null,
      trafficReqSeq: 0,
      latestTrafficReqSeq: 0
    };
  },
  mounted() {
    this.initGraphInfo();
    this.fetchBandwidthList();
  },
  beforeDestroy() {
    this.clearMonitorPolling();
    if (this.$api && typeof this.$api.removeCancel === 'function') {
      this.$api.removeCancel(BANDWIDTH_TRAFFIC_CANCEL_NAME);
    }
  },
  methods: {
    /**
     * 初始化图表图例信息
     */
    initGraphInfo() {
      this.yAxisName = this.$t('bwTraffic.trafficUnit');
      this.seriesNames = `${this.$t('bwTraffic.totalUpRealTraffic')},${this.$t('bwTraffic.totalDownRealTraffic')}`;
    },

    /**
     * 拉取客户名下带宽资源列表，作为下拉数据源
     */
    fetchBandwidthList() {
      this.loading = true;
      const params = {
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE
      };
      this.$api.getBandWidthList(params).then((res) => {
        if (res && res.errno === 10000 && res.data && Array.isArray(res.data.bandwidths)) {
          // 过滤掉为 null 的脏数据
          const list = res.data.bandwidths.filter(item => item && item.bandwidthId);
          this.bandwidthList = list;
          if (list.length) {
            this.selectedBandwidthId = list[0].bandwidthId;
            this.selectedBandwidth = list[0];
            // 初次加载默认拉取 24h 监控数据
            this.onDateRangeChange(false);
          }
        } else {
          this.bandwidthList = [];
        }
      }).catch(() => {
        // 接口异常时兜底为空数组，避免页面崩溃
        this.bandwidthList = [];
      }).finally(() => {
        this.loading = false;
      });
    },

    /**
     * 带宽切换
     */
    onBandwidthChange(val) {
      const matched = this.bandwidthList.find(item => item.bandwidthId === val);
      this.selectedBandwidth = matched || null;
      // 切换带宽时重置图表
      this.dynamicEnvData = null;
      this.isChartEmpty = false;
      this.periodTag = '--';
      this.onDateRangeChange(false);
    },

    /**
     * 启动轮询：根据 periodTag 自适应轮询间隔
     */
    startMonitorPolling() {
      try {
        this.clearMonitorPolling();
        const tag = this.periodTag;
        let interval = 60000; // 默认 1min
        if (tag && tag !== '--') {
          const num = parseInt(tag, 10);
          if (Number.isFinite(num) && num > 0) {
            const unit = tag.replace(num, '');
            if (unit === 's') interval = num * 1000;
            if (unit === 'min') interval = num * 60 * 1000;
          }
        }
        this.monitorPollTimer = setTimeout(() => {
          this.onDateRangeChange(true);
        }, interval);
      } catch (e) {
        // 启动轮询失败不影响主流程
      }
    },

    /**
     * 清理轮询定时器
     */
    clearMonitorPolling() {
      if (!this.monitorPollTimer) return;
      clearTimeout(this.monitorPollTimer);
      this.monitorPollTimer = null;
    },

    /**
     * 周期对齐：根据 periodTag 计算毫秒时长
     */
    getPeriodDuration() {
      const tag = this.periodTag;
      if (!tag || tag === '--') return 0;
      const num = parseInt(tag, 10);
      if (!Number.isFinite(num) || num <= 0) return 0;
      const unit = tag.replace(num, '');
      if (unit === 's') return num * 1000;
      if (unit === 'min') return num * 60 * 1000;
      return 0;
    },

    alignTimestampByPeriod(ts) {
      const periodDuration = this.getPeriodDuration();
      if (!periodDuration || !Number.isFinite(ts)) return ts;
      return Math.floor(ts / periodDuration) * periodDuration;
    },

    /**
     * 默认24h时间区间
     */
    getDefaultTime() {
      const now = Date.now();
      return [now - ONE_HOUR * 24, now];
    },

    /**
     * 时间范围变化 / 主请求逻辑
     * @param {boolean} isPolling 是否轮询触发
     */
    onDateRangeChange(isPolling = false) {
      if (!this.selectedBandwidth) return;
      const { bandwidthId, regionEn: idcId } = this.selectedBandwidth;
      if (!bandwidthId || !idcId) return;

      let [startTime, endTime] = [null, null];
      if (this.dateRangeValue === '' || this.dateRangeValue == null) {
        this.defaultValue = '24h';
        if (!isPolling) {
          [startTime, endTime] = this.getDefaultTime();
        } else {
          [startTime, endTime] = this.getDefaultTime();
        }
      } else {
        [startTime, endTime] = this.dateRangeValue;
        this.defaultValue = '';
        if (isPolling) {
          const rangeDuration = endTime - startTime;
          const now = Date.now();
          endTime = this.alignTimestampByPeriod(now);
          if (rangeDuration > 0) startTime = endTime - rangeDuration;
        }
      }

      // 极端校验：startTime / endTime 非法
      if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
        this.dynamicEnvData = null;
        return;
      }

      // 超过31天提示
      if (endTime - startTime > ONE_HOUR * 24 * MAX_DATE_RANGE) {
        this.dateRangeTips = true;
        return;
      }
      this.dateRangeTips = false;

      const reqParam = {
        bandwidthId,
        startTime,
        endTime,
        idc: idcId,
        all: 1
      };

      const reqSeq = ++this.trafficReqSeq;
      this.latestTrafficReqSeq = reqSeq;
      this.chartLoading = !isPolling || this.chartLoading;

      if (this.$api.removeCancel) {
        this.$api.removeCancel(BANDWIDTH_TRAFFIC_CANCEL_NAME);
      }

      let keepLoadingOnCancel = false;
      this.$api.getBandwidthTrafficInfo(reqParam).then((response) => {
        if (reqSeq !== this.latestTrafficReqSeq) return;
        const { data } = response || {};
        const { bandwidthTraffics, granularity } = data || {};
        this.formatTrafficData(bandwidthTraffics);
        this.periodTag = granularity || '--';
        this.clearMonitorPolling();
        this.startMonitorPolling();
      }).catch((err) => {
        if (reqSeq !== this.latestTrafficReqSeq) return;
        if (err && err.__CANCEL__) {
          keepLoadingOnCancel = true;
          this.chartLoading = true;
          return;
        }
        this.dynamicEnvData = null;
      }).finally(() => {
        if (reqSeq !== this.latestTrafficReqSeq) return;
        if (keepLoadingOnCancel) return;
        this.chartLoading = false;
      });
    },

    /**
     * 空数据兜底图表
     */
    showEmptyChart() {
      let [startTime, endTime] = [null, null];
      if (!this.dateRangeValue) {
        [startTime, endTime] = this.getDefaultTime();
      } else {
        [startTime, endTime] = this.dateRangeValue;
      }
      try {
        this.dynamicEnvData = ChartHelper.getEmptyData(startTime, endTime);
      } catch (e) {
        this.dynamicEnvData = null;
      }
      this.isChartEmpty = true;
    },

    /**
     * 把接口返回的流量对象格式化为折线图组件期望的结构
     */
    formatTrafficData(obj) {
      // 空数组：兜底空图
      if (!obj || (Array.isArray(obj) && !obj.length)) {
        this.dynamicEnvData = null;
        this.showEmptyChart();
        return;
      }
      this.isChartEmpty = false;
      const dynamicEnvDataCopy = {};
      Object.keys(obj).forEach((key) => {
        const item = obj[key] || {};
        dynamicEnvDataCopy[key] = [];
        dynamicEnvDataCopy[key].push(item.in);
        dynamicEnvDataCopy[key].push(item.out);
      });
      this.dynamicEnvData = dynamicEnvDataCopy;
    }
  }
};
</script>

<style lang="scss" scoped>
.resource-monitor {
  background: #fff;
  padding: 20px;
  border-radius: 8px;

  .monitor-title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    margin: 0 0 16px 0;
  }

  .filter-row {
    margin-bottom: 10px;
    display: flex;
    align-items: center;

    .filter-label {
      line-height: 30.4px;
      font-size: 13px;
      color: rgba(0,0,0,0.65);
    }
  }

  .date-range-tips {
    display: inline-block;
    margin-left: 10px;
    line-height: 30px;
    color: #212121;
    line-height: 20px;
    font-size: 12px;
  }

  .date-range-color {
    color: red;
  }

  .chart-box {
    height: 430px;
    // height: 460px;
    margin: 15px 0 0;

    .chart-header {
      background: #f6fafd;
      padding: 4px 9px;
      display: flex;
      justify-content: flex-end;

      .period {
        line-height: 22px;
        margin: 1px 0 0 1px;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.65);
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/resourceOverview/components/ResourceMonitor.vue